import '../../styles/vendorStyles';
import '../../styles/main.css';
import './App.css';
import './styles/adminlte-buttons-fix.css';
import './styles/default.css';
import './styles/BootstrapXL.css';
import './nav-tabs.css';
import './styles/redesignDefaultStyles.css';
import './styles/react-select.css';
import './styles/box.css';
import './styles/form.css';
import { setContentWrapper } from 'components/GlobalState/commands/setContentWrapper';
import { selectAuthenticatedAndLicensed } from 'components/GlobalState/selectors/selectAuthenticatedAndLicensed';

import { memo, useEffect } from 'react';
import type { FC } from 'react';

import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import { prop } from '@ipfabric/utils/fp/prop';
import { usePostHog } from 'app/analytics/usePostHog';
import Chart from 'chart.js';
import classNames from 'classnames';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { setContentAfterContainer } from 'components/GlobalState/commands/setContentAfterContainer';
import { useLicenseChecker } from 'components/GlobalState/useLicenseChecker';
import { SessionExtensionDialog } from 'components/SessionExtensionDialog/SessionExtensionDialog';
import { Sidebar } from 'components/Sidebar/Sidebar';
import { SystemUnavailableOverlay } from 'components/SystemUnavailableOverlay/SystemUnavailableOverlay';
import { useLocale } from 'containers/App/LocaleContext';
import { useSnapshotFromUrl } from 'containers/App/useSnapshotFromUrl';
import { AppLoader } from 'containers/AppLoader/AppLoader';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import ErrorBoundary from 'containers/ErrorBoundary/ErrorBoundary';
import { Notifications } from 'containers/Toaster';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useErrorHandler } from 'utils/hooks/useErrorHandler';
import { Header } from './Header/Header';
import { RoutesConfig } from './Routes/RoutesConfig';
import { REFRESH_DIALOGUE_NAME } from './constants';

const useInitializeChartJs = () =>
  // any is used on many places here just to satisfy legacy code, official chart.js types
  // do not work with our implementation.
  useEffect(() => {
    Chart.pluginService.register({
      beforeRender: (chart: any) => {
        if (chart.config.options!.showAllTooltips) {
          // create an array of tooltips
          // we can't use the chart tooltip because there is only one tooltip per chart
          chart.pluginTooltips = [];
          chart.config.data.datasets.forEach((_dataset: any, i: number) => {
            for (const sector of chart.getDatasetMeta(i).data) {
              chart.pluginTooltips.push(
                new Chart.Tooltip(
                  {
                    _chart: chart.chart,
                    _chartInstance: chart,
                    _data: chart.data,
                    _options: chart.options.tooltips,
                    _active: [sector],
                  },
                  chart,
                ),
              );
            }
          });
          // turn off normal tooltips
          chart.options.tooltips.enabled = false;
        }
      },
      afterDraw: (chart: any, easing: any) => {
        if (chart.config.options.showAllTooltips) {
          // we don't want the permanent tooltips to animate, so don't do anything till the animation runs atleast once
          if (!chart.allTooltipsOnce) {
            if (easing !== 1) {
              return;
            }
            chart.allTooltipsOnce = true;
          }

          // turn on tooltips
          chart.options.tooltips.enabled = true;
          Chart.helpers.each(chart.pluginTooltips, (tooltip: any) => {
            tooltip.initialize();
            tooltip.update();
            // we don't actually need this since we are not animating tooltips
            tooltip.pivot();
            tooltip.transition(easing).draw();
          });
          chart.options.tooltips.enabled = false;
        }
      },
    });

    Chart.pluginService.register({
      // Text in middle
      beforeDraw: (chart: any) => {
        if (chart.config.options.showTextInMiddle) {
          const { width } = chart.chart;
          const { ctx } = chart.chart;

          ctx.restore();
          const fontSize = 1.5; // (height / 114).toFixed(2)
          ctx.font = `${fontSize}em sans-serif`;
          ctx.textBaseline = 'top';
          const text = `${chart.config.data.datasets[0].data[0]}${
            chart.config.options.absolute === false ? '%' : ''
          }`;
          const textX = Math.round((width - ctx.measureText(text).width) / 2);
          // const textY =
          //   height / 2 -
          //   (152 - chart.chartArea.bottom > 0 ? Math.floor((152 - chart.chartArea.bottom) / 3) : 0) -
          //   20;
          const textY =
            chart.chartArea.bottom -
            (chart.chartArea.bottom - chart.chartArea.top - 101) / 2 -
            20;
          ctx.fillText(text, textX, textY);
          ctx.save();
        }
      },
    });
  }, []);

const BareApp: FC = () => {
  const msg = useLocale();
  useErrorHandler(msg);

  const globalStateCtxProps = useGlobalStateContextSelectors({
    authenticatedAndLicensed: selectAuthenticatedAndLicensed,
    auth: prop('auth'),
    dateTimeFormat: prop('dateTimeFormat'),
    dispatch: prop('dispatch'),
    ipfNewVersion: prop('ipfNewVersion'),
    license: prop('license'),
    loginStatus: prop('loginStatus'),
    minimisedMenu: prop('minimisedMenu'),
    user: prop('user'),
    version: prop('version'),
  });
  const licenseChecker = useLicenseChecker();
  const location = useLocation();

  const getMenuClass = () => {
    if (
      globalStateCtxProps.authenticatedAndLicensed &&
      globalStateCtxProps.minimisedMenu
    ) {
      return ' appPage--sidebar-collapsed';
    }
    return '';
  };

  const handleRefreshDialogClickConfirm = () => {
    window.location.reload();
  };

  useInitializeChartJs();

  usePostHog();

  const snapshotFromUrl = useSnapshotFromUrl();
  if (snapshotFromUrl.willUpdateSnapshot) {
    // do not render content to avoid loading of data for snapshot that won't be used
    return <AppLoader />;
  }

  return (
    <>
      <ErrorBoundary pathname={location.pathname}>
        <ScrollView contentClassName='flex flex-col z-0'>
          <div
            className={classNames('wrapper appPage fill', getMenuClass())}
            data-pathname={location.pathname}
          >
            <Helmet
              titleTemplate='%s - IP Fabric network infrastructure controller - IPFabric'
              defaultTitle='IP Fabric network infrastructure controller - IPFabric'
            >
              <meta
                name='description'
                content='IP Fabric network infrastructure controller - IPFabric'
              />
            </Helmet>
            <Header
              authenticatedAndLicensed={
                globalStateCtxProps.authenticatedAndLicensed
              }
              dateTimeFormat={globalStateCtxProps.dateTimeFormat}
              licenseChecker={licenseChecker}
              licenseInfo={globalStateCtxProps.license}
              loginStatus={globalStateCtxProps.loginStatus}
              msg={msg}
              newVersion={globalStateCtxProps.ipfNewVersion}
              timezone={globalStateCtxProps.user.timezone}
              user={globalStateCtxProps.user}
              version={globalStateCtxProps.version}
              auth={globalStateCtxProps.auth}
            />
            {globalStateCtxProps.authenticatedAndLicensed && <Sidebar />}
            <div className='flex flex-1'>
              <div
                className={classNames(
                  'content-wrapper relative flex-1 bg-colored-light',
                  globalStateCtxProps.authenticatedAndLicensed && 'loggedin',
                )}
                ref={(contentWrapper) => {
                  globalStateCtxProps.dispatch(setContentWrapper, {
                    contentWrapper,
                  });
                }}
              >
                <ScrollView contentClassName='flex flex-col'>
                  <RoutesConfig />
                </ScrollView>
              </div>
              <div
                className='flex flex-col'
                ref={(contentAfterContainer) => {
                  globalStateCtxProps.dispatch(setContentAfterContainer, {
                    contentAfterContainer,
                  });
                }}
              />
            </div>
          </div>
        </ScrollView>
        <Notifications />
        <SessionExtensionDialog />
      </ErrorBoundary>
      {/* Rendered outside of ErrorBoundary to stay available even after a crash, e.g. during system upgrade */}
      <ConfirmDialog
        closable={false}
        id={REFRESH_DIALOGUE_NAME}
        msg={msg.app.refreshDialog}
        onClickConfirm={handleRefreshDialogClickConfirm}
        btnCancelAccent='positive'
        btnCancelVariant='secondary'
        btnConfirmAccent='positive'
        btnConfirmVariant='primary'
      />
      <SystemUnavailableOverlay />
    </>
  );
};

export const App = memo(BareApp);
